import { useState, useRef, forwardRef } from 'react'
import { CameraIcon, PhotoIcon, FolderIcon, XMarkIcon, ChevronLeftIcon, ChevronRightIcon, TrashIcon, ArrowPathIcon, LightBulbIcon } from '@heroicons/react/24/outline'
import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'
import { supplementsAPI } from '../../services/api'
import { contentLanguage } from '../../i18n/supported'

const PHOTO_ANALYSIS_TIMEOUT_MS = 45000

const SupplementPhotoUpload = forwardRef(({ onAnalysisComplete, onCancel }, _ref) => {
  const { t, i18n } = useTranslation()
  const [selectedFile, setSelectedFile] = useState(null)
  const [selectedFiles, setSelectedFiles] = useState([]) // For batch processing
  const [currentFileIndex, setCurrentFileIndex] = useState(0)
  const [previewUrl, setPreviewUrl] = useState(null)
  const [uploadMode, setUploadMode] = useState(null) // 'camera' | 'file' | 'folder'
  const [analysisPhase, setAnalysisPhase] = useState(null) // 'uploading' | 'analyzing'
  const [showTips, setShowTips] = useState(false)
  const [lastError, setLastError] = useState(null)
  const fileInputRef = useRef(null)
  const folderInputRef = useRef(null)
  const abortControllerRef = useRef(null)

  // Mutation for photo analysis
  const analyzeMutation = useMutation({
    mutationFn: (formData) => {
      setAnalysisPhase('uploading')
      const controller = new AbortController()
      abortControllerRef.current = controller
      const timeoutId = setTimeout(() => controller.abort(), PHOTO_ANALYSIS_TIMEOUT_MS)

      return supplementsAPI.analyzeSupplementPhoto(formData, controller.signal)
        .finally(() => clearTimeout(timeoutId))
    },
    onMutate: () => {
      setLastError(null)
      setAnalysisPhase('uploading')
      setTimeout(() => setAnalysisPhase((prev) => prev ? 'analyzing' : null), 1500)
    },
    onError: (error) => {
      setAnalysisPhase(null)
      console.error('Photo analysis failed:', error)
      const message = error.name === 'CanceledError' || error.code === 'ERR_CANCELED'
        ? t('supplements.upload.errors.timeout', 'Analysis timed out. Please try again.')
        : error.response?.data?.detail || t('supplements.upload.errors.analysisFailed')
      setLastError(message)
      toast.error(message)
    },
    onSuccess: () => {
      setAnalysisPhase(null)
    }
  })

  // Handle file selection
  const handleFileSelect = (event) => {
    const file = event.target.files[0]
    if (file) {
      processFile(file)
    }
  }

  // Handle folder selection
  const handleFolderSelect = (event) => {
    const files = Array.from(event.target.files)
    if (files.length > 0) {
      // Filter for valid image files
      const imageFiles = files.filter(file => {
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/heic']
        return allowedTypes.includes(file.type)
      })

      if (imageFiles.length === 0) {
        toast.error(t('supplements.upload.errors.noImagesInFolder'))
        return
      }

      console.log(`[SupplementPhotoUpload] Selected ${imageFiles.length} files for batch analysis`)

      // Store all files for batch processing
      setSelectedFiles(imageFiles)
      setCurrentFileIndex(0)

      // Create preview for first file
      processFile(imageFiles[0])
    }
  }

  // Process selected file
  const processFile = (file) => {
    console.log('[SupplementPhotoUpload] Processing file:', file.name, file.type, file.size)

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/heic']
    if (!allowedTypes.includes(file.type)) {
      console.error('[SupplementPhotoUpload] Invalid file type:', file.type)
      toast.error(t('supplements.upload.errors.invalidFileType'))
      return
    }

    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024 // 10MB
    if (file.size > maxSize) {
      console.error('[SupplementPhotoUpload] File too large:', file.size)
      toast.error(t('supplements.upload.errors.fileTooLarge'))
      return
    }

    console.log('[SupplementPhotoUpload] File validated, setting selectedFile')
    setSelectedFile(file)

    // Create preview URL
    console.log('[SupplementPhotoUpload] Creating preview URL')
    const reader = new FileReader()
    reader.onloadend = () => {
      console.log('[SupplementPhotoUpload] Preview URL created, setting previewUrl')
      setPreviewUrl(reader.result)
    }
    reader.onerror = (error) => {
      console.error('[SupplementPhotoUpload] FileReader error:', error)
    }
    reader.readAsDataURL(file)
  }

  // Handle upload and analysis
  const handleAnalyze = () => {
    if (!selectedFile) return

    const formData = new FormData()

    // Check if we have multiple files (batch mode)
    if (selectedFiles.length > 1) {
      console.log(`[SupplementPhotoUpload] Batch analysis: sending ${selectedFiles.length} files`)
      // Add all files to FormData for batch analysis
      selectedFiles.forEach(file => {
        formData.append('files', file)
      })
    } else {
      // Single file mode
      console.log(`[SupplementPhotoUpload] Single file analysis`)
      formData.append('files', selectedFile)
    }

    analyzeFile(formData)
  }

  // Separate analyze function to avoid duplication
  const analyzeFile = (formData) => {
    analyzeMutation.mutate(formData, {
      onSuccess: async (response) => {
        console.log('[SupplementPhotoUpload] Analysis complete:', response.data)

        let resultData = response.data

        // If user's language is not English, translate the analysis on-demand
        const currentLanguage = contentLanguage(i18n?.language)
        if (currentLanguage !== 'en' && resultData.photo_analysis_id) {
          try {
            console.log(`[SupplementPhotoUpload] Translating to ${currentLanguage}...`)
            const translationResponse = await supplementsAPI.translateAnalysis(
              resultData.photo_analysis_id,
              currentLanguage
            )
            resultData = translationResponse.data
            console.log('[SupplementPhotoUpload] Translation complete:', resultData)
          } catch (translationError) {
            console.warn('[SupplementPhotoUpload] Translation failed, using original:', translationError)
            // Continue with original English results if translation fails
          }
        }

        // Call parent onAnalysisComplete with (translated) results
        if (onAnalysisComplete) {
          onAnalysisComplete(resultData)
        }
      }
    })
  }

  // Clear selection
  const handleClear = () => {
    setSelectedFile(null)
    setSelectedFiles([])
    setCurrentFileIndex(0)
    setPreviewUrl(null)
    setUploadMode(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
    if (folderInputRef.current) folderInputRef.current.value = ''
  }

  // Navigate to previous photo
  const handlePrevPhoto = () => {
    if (currentFileIndex > 0) {
      const newIndex = currentFileIndex - 1
      setCurrentFileIndex(newIndex)
      processFile(selectedFiles[newIndex])
    }
  }

  // Navigate to next photo
  const handleNextPhoto = () => {
    if (currentFileIndex < selectedFiles.length - 1) {
      const newIndex = currentFileIndex + 1
      setCurrentFileIndex(newIndex)
      processFile(selectedFiles[newIndex])
    }
  }

  // Remove current photo from batch
  const handleRemoveCurrentPhoto = () => {
    if (selectedFiles.length <= 1) {
      // If only one photo left, clear everything
      handleClear()
      return
    }

    const newFiles = selectedFiles.filter((_, index) => index !== currentFileIndex)
    setSelectedFiles(newFiles)

    // Adjust current index if needed
    const newIndex = currentFileIndex >= newFiles.length ? newFiles.length - 1 : currentFileIndex
    setCurrentFileIndex(newIndex)
    processFile(newFiles[newIndex])
  }

  return (
    <div className="space-y-4">
      {/* Upload Mode Selection */}
      {!uploadMode && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Camera Option */}
          <button
            onClick={() => {
              setUploadMode('camera')
              fileInputRef.current?.click()
            }}
            className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-gray-300 rounded-lg hover:border-indigo-500 hover:bg-indigo-50 transition-colors"
          >
            <CameraIcon className="h-12 w-12 text-gray-400 mb-3" />
            <span className="text-sm font-medium text-gray-900">{t('supplements.upload.takePhoto')}</span>
            <span className="text-xs text-gray-500 mt-1">{t('supplements.upload.useCamera')}</span>
          </button>

          {/* File Upload Option */}
          <button
            onClick={() => {
              setUploadMode('file')
              fileInputRef.current?.click()
            }}
            className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-gray-300 rounded-lg hover:border-indigo-500 hover:bg-indigo-50 transition-colors"
          >
            <PhotoIcon className="h-12 w-12 text-gray-400 mb-3" />
            <span className="text-sm font-medium text-gray-900">{t('supplements.upload.uploadPhoto')}</span>
            <span className="text-xs text-gray-500 mt-1">{t('supplements.upload.chooseFromDevice')}</span>
          </button>

          {/* Folder Upload Option */}
          <button
            onClick={() => {
              setUploadMode('folder')
              folderInputRef.current?.click()
            }}
            className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-gray-300 rounded-lg hover:border-indigo-500 hover:bg-indigo-50 transition-colors"
          >
            <FolderIcon className="h-12 w-12 text-gray-400 mb-3" />
            <span className="text-sm font-medium text-gray-900">{t('supplements.upload.uploadFolder')}</span>
            <span className="text-xs text-gray-500 mt-1">{t('supplements.upload.forTesting')}</span>
          </button>
        </div>
      )}

      {/* Hidden file inputs */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/heic"
        capture={uploadMode === 'camera' ? 'environment' : undefined}
        onChange={handleFileSelect}
        className="hidden"
      />
      <input
        ref={folderInputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/heic"
        multiple
        webkitdirectory=""
        directory=""
        onChange={handleFolderSelect}
        className="hidden"
      />

      {/* Loading state during batch processing */}
      {uploadMode && !previewUrl && (
        <div className="flex flex-col items-center justify-center p-12 border-2 border-dashed border-gray-300 rounded-lg">
          <svg className="animate-spin h-12 w-12 text-indigo-600 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="text-gray-600">{t('supplements.upload.loadingPhoto')}</p>
        </div>
      )}

      {/* Preview and Analysis */}
      {previewUrl && (
        <div className="space-y-4">
          <div className="relative">
            <img
              src={previewUrl}
              alt="Supplement preview"
              className="w-full max-h-96 object-contain rounded-lg border-2 border-gray-200"
            />
            <button
              onClick={handleClear}
              className="absolute top-2 right-2 p-2 bg-surface-container rounded-full shadow-lg hover:bg-gray-100"
            >
              <XMarkIcon className="h-5 w-5 text-gray-600" />
            </button>
          </div>

          {/* Photo navigation for batch mode */}
          {selectedFiles.length > 1 && (
            <div className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
              <button
                onClick={handlePrevPhoto}
                disabled={currentFileIndex === 0}
                className="p-2 rounded-full hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronLeftIcon className="h-6 w-6 text-gray-600" />
              </button>

              <div className="flex items-center space-x-4">
                <span className="text-sm font-medium text-indigo-600">
                  {t('supplements.upload.photoOf', { current: currentFileIndex + 1, total: selectedFiles.length })}
                </span>
                <button
                  onClick={handleRemoveCurrentPhoto}
                  className="flex items-center space-x-1 px-3 py-1 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  title={t('supplements.upload.removePhoto')}
                >
                  <TrashIcon className="h-4 w-4" />
                  <span>{t('supplements.upload.remove')}</span>
                </button>
              </div>

              <button
                onClick={handleNextPhoto}
                disabled={currentFileIndex === selectedFiles.length - 1}
                className="p-2 rounded-full hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronRightIcon className="h-6 w-6 text-gray-600" />
              </button>
            </div>
          )}

          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              <p className="font-medium">{selectedFile?.name}</p>
              <p className="text-xs text-gray-500">
                {(selectedFile?.size / 1024).toFixed(1)} KB
              </p>
            </div>
          </div>

          {/* Photo Tips */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg">
            <button
              type="button"
              onClick={() => setShowTips(!showTips)}
              className="w-full flex items-center justify-between px-3 py-2 text-sm text-amber-800"
            >
              <span className="flex items-center gap-2">
                <LightBulbIcon className="h-4 w-4" />
                {t('supplements.upload.tipsTitle', 'Photo Tips')}
              </span>
              <span className="text-xs">{showTips ? '-' : '+'}</span>
            </button>
            {showTips && (
              <ul className="px-3 pb-3 text-xs text-amber-700 space-y-1 list-disc list-inside">
                <li>{t('supplements.upload.tips.lighting', 'Ensure good, even lighting')}</li>
                <li>{t('supplements.upload.tips.label', 'Capture the full ingredient label')}</li>
                <li>{t('supplements.upload.tips.glare', 'Avoid glare on the label')}</li>
                <li>{t('supplements.upload.tips.focus', 'Keep the camera steady and in focus')}</li>
              </ul>
            )}
          </div>

          {/* Error with Retry */}
          {lastError && !analyzeMutation.isPending && (
            <div className="flex items-center justify-between bg-red-50 border border-red-200 rounded-lg px-4 py-3">
              <p className="text-sm text-red-700">{lastError}</p>
              <button
                onClick={handleAnalyze}
                className="flex items-center gap-1 ml-3 px-3 py-1.5 text-sm font-medium text-red-700 bg-red-100 hover:bg-red-200 rounded-md transition-colors"
              >
                <ArrowPathIcon className="h-4 w-4" />
                {t('supplements.upload.retry', 'Retry')}
              </button>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              onClick={handleAnalyze}
              disabled={analyzeMutation.isPending}
              className="flex-1 btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {analyzeMutation.isPending ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {analysisPhase === 'uploading'
                    ? t('supplements.upload.uploading', 'Uploading...')
                    : selectedFiles.length > 1
                      ? t('supplements.upload.analyzingPhotos', { count: selectedFiles.length })
                      : t('supplements.upload.analyzing')}
                </>
              ) : (
                selectedFiles.length > 1 ? t('supplements.upload.analyzePhotos', { count: selectedFiles.length }) : t('supplements.upload.analyzeSupplement')
              )}
            </button>
            <button
              onClick={handleClear}
              disabled={analyzeMutation.isPending}
              className="btn-outline disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {selectedFiles.length > 1 ? t('supplements.upload.chooseDifferentPhotos') : t('supplements.upload.chooseDifferentPhoto')}
            </button>
          </div>
        </div>
      )}

      {/* Cancel Button */}
      {onCancel && (
        <div className="flex justify-end">
          <button
            onClick={onCancel}
            className="text-sm text-gray-600 hover:text-gray-900"
          >
            {t('common.cancel')}
          </button>
        </div>
      )}
    </div>
  )
})

SupplementPhotoUpload.displayName = 'SupplementPhotoUpload'

export default SupplementPhotoUpload
